/**
 * ProposalMemoryPane — Read-only proposal memory surface
 *
 * Displays proposal-memory artifacts as inspectable metadata only.
 * Demonstrates how future governed learning/proposal systems can attach
 * to immutable lineage safely.
 *
 * SAFETY: Read-only inspection only. No execution authority, no deployment controls,
 * no autonomous learning, no state mutation, no recursive self-modification.
 */

import React from 'react';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface ProposalMemoryPaneProps {
  model?: {
    entries?: ProposalMemoryEntry[];
  };
}

interface ProposalMemoryEntry {
  proposalId: string;
  proposalType: string;
  createdAt: string;
  lineageSource: string;
  continuityReference: string;
  admissibilityStatus: 'ADMITTED' | 'REFUSED' | 'PENDING' | 'UNKNOWN';
  humanReviewRequired: true;
  executionAuthority: false;
  replayable: true;
  immutableReference: true;
}

const MOCK_ENTRIES: ProposalMemoryEntry[] = [
  {
    proposalId: 'prop-001-code-complexity-reduction',
    proposalType: 'CODE_COMPLEXITY_REDUCTION',
    createdAt: '2026-01-30T05:09:49Z',
    lineageSource: 'evolution/learning-receipts/41ef2dd8e6892eeaa1764af77a743ca8bb0168a3258d04a3b368afc98bf26649.json',
    continuityReference: 'epoch_20260127_231223_eb2d4ef5b179',
    admissibilityStatus: 'ADMITTED',
    humanReviewRequired: true,
    executionAuthority: false,
    replayable: true,
    immutableReference: true,
  },
  {
    proposalId: 'prop-002-dead-code-elimination',
    proposalType: 'DEAD_CODE_ELIMINATION',
    createdAt: '2026-01-30T05:15:55Z',
    lineageSource: 'evolution/learning-receipts/82066ca9a99a513b64eef4b0d567d5c07a86e53812bb03c4b8aa3731a885ef08.json',
    continuityReference: 'epoch_20260127_231223_eb2d4ef5b179',
    admissibilityStatus: 'PENDING',
    humanReviewRequired: true,
    executionAuthority: false,
    replayable: true,
    immutableReference: true,
  },
  {
    proposalId: 'prop-003-deterministic-surface-hardening',
    proposalType: 'DETERMINISTIC_SURFACE_HARDENING',
    createdAt: '2026-01-30T06:21:24Z',
    lineageSource: 'evolution/learning-receipts/ed21edb79e585b89792c9045ca9c24e055bf86b6b56ccd3ffa7158376b0cce70.json',
    continuityReference: 'epoch_20260127_231223_eb2d4ef5b179',
    admissibilityStatus: 'REFUSED',
    humanReviewRequired: true,
    executionAuthority: false,
    replayable: true,
    immutableReference: true,
  },
];

export const ProposalMemoryPane: React.FC<ProposalMemoryPaneProps> = ({ model }) => {
  const entries = model?.entries || MOCK_ENTRIES;

  const truncateHash = (hash: string) => {
    if (!hash) return '—';
    if (hash.length <= 16) return hash;
    return `${hash.slice(0, 12)}…`;
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '—';
    return timestamp.slice(0, 19).replace('T', ' ');
  };

  const getAdmissibilityColor = (status: string) => {
    if (status === 'ADMITTED') return SEMANTIC.stabilized;
    if (status === 'REFUSED') return SEMANTIC.refusal;
    if (status === 'PENDING') return SEMANTIC.neutral;
    return SEMANTIC.neutral;
  };

  const renderProposalEntry = (entry: ProposalMemoryEntry) => (
    <div key={entry.proposalId} style={{
      padding: SPACING.sp2,
      backgroundColor: NEUTRAL.bg600,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
      borderRadius: 2,
      marginBottom: SPACING.sp2,
    }}>
      {/* Header: Proposal ID and Type */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: SPACING.sp2,
        paddingBottom: SPACING.sp1,
        borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.caption.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          fontFamily: TYPOGRAPHY.mono.family,
        }}>
          {entry.proposalId}
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: getAdmissibilityColor(entry.admissibilityStatus).text,
          backgroundColor: getAdmissibilityColor(entry.admissibilityStatus).chip,
          padding: '1px 4px',
          borderRadius: 2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          {entry.admissibilityStatus}
        </span>
      </div>

      {/* Proposal Type */}
      <div style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        marginBottom: SPACING.sp1,
      }}>
        <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>Type: </span>
        {entry.proposalType}
      </div>

      {/* Created At */}
      <div style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        marginBottom: SPACING.sp1,
      }}>
        <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>Created: </span>
        {formatTimestamp(entry.createdAt)}
      </div>

      {/* Lineage Source */}
      <div style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        marginBottom: SPACING.sp1,
      }}>
        <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>Lineage: </span>
        <span style={{ fontFamily: TYPOGRAPHY.mono.family }}>
          {truncateHash(entry.lineageSource)}
        </span>
      </div>

      {/* Continuity Reference */}
      <div style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        marginBottom: SPACING.sp1,
      }}>
        <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>Continuity: </span>
        <span style={{ fontFamily: TYPOGRAPHY.mono.family }}>
          {entry.continuityReference}
        </span>
      </div>

      {/* Safety Flags */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: SPACING.sp2,
        marginTop: SPACING.sp2,
        paddingTop: SPACING.sp1,
        borderTop: `1px solid ${NEUTRAL.borderSubtle}`,
        fontSize: TYPOGRAPHY.micro.size,
      }}>
        <span style={{ color: entry.humanReviewRequired ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
          humanReviewRequired: {String(entry.humanReviewRequired)}
        </span>
        <span style={{ color: !entry.executionAuthority ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
          executionAuthority: {String(entry.executionAuthority)}
        </span>
        <span style={{ color: entry.replayable ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
          replayable: {String(entry.replayable)}
        </span>
        <span style={{ color: entry.immutableReference ? SEMANTIC.stabilized.text : NEUTRAL.text400 }}>
          immutableReference: {String(entry.immutableReference)}
        </span>
      </div>
    </div>
  );

  return (
    <div style={{
      padding: SPACING.sp3,
      height: '100%',
      overflowY: 'auto',
      backgroundColor: NEUTRAL.bg200,
    }}>
      {/* Header */}
      <div style={{
        marginBottom: SPACING.sp3,
        paddingBottom: SPACING.sp2,
        borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp1,
        }}>
          PROPOSAL MEMORY
        </div>
        <div style={{
          fontSize: TYPOGRAPHY.caption.size,
          color: NEUTRAL.text400,
        }}>
          Read-only proposal memory surface for inspectable metadata
        </div>
      </div>

      {/* Safety Boundaries */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          SAFETY BOUNDARIES
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: SPACING.sp2,
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            READ-ONLY
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            IMMUTABLE REFERENCE
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            NO EXECUTION AUTHORITY
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            HUMAN REVIEW REQUIRED
          </span>
        </div>
      </div>

      {/* Proposal Memory Summary */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          PROPOSAL MEMORY SUMMARY
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
        }}>
          <div>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Total Proposals
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              {entries.length}
            </div>
          </div>
          <div>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Admitted
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
            }}>
              {entries.filter(e => e.admissibilityStatus === 'ADMITTED').length}
            </div>
          </div>
          <div>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Pending
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: NEUTRAL.text400,
            }}>
              {entries.filter(e => e.admissibilityStatus === 'PENDING').length}
            </div>
          </div>
          <div>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Refused
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: SEMANTIC.refusal.text,
            }}>
              {entries.filter(e => e.admissibilityStatus === 'REFUSED').length}
            </div>
          </div>
        </div>
      </div>

      {/* Replayability Status */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          REPLAYABILITY STATUS
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp1,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <span>Replayable Lineage References</span>
            <span style={{
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
            }}>
              {entries.every(e => e.replayable) ? 'true' : 'mixed'}
            </span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <span>Immutable Reference</span>
            <span style={{
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
            }}>
              {entries.every(e => e.immutableReference) ? 'true' : 'mixed'}
            </span>
          </div>
        </div>
      </div>

      {/* Human Review Requirement */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          HUMAN REVIEW REQUIREMENT
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: SPACING.sp1,
          backgroundColor: NEUTRAL.bg700,
          borderRadius: 2,
          fontSize: TYPOGRAPHY.micro.size,
        }}>
          <span style={{ color: NEUTRAL.text400 }}>Human Review Required</span>
          <span style={{
            fontWeight: 600,
            color: entries.every(e => e.humanReviewRequired) ? SEMANTIC.stabilized.text : SEMANTIC.refusal.text,
          }}>
            {entries.every(e => e.humanReviewRequired) ? 'true' : 'mixed'}
          </span>
        </div>
      </div>

      {/* Admissibility Overview */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          ADMISSION OVERVIEW
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp1,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <span>Admission Rate</span>
            <span style={{
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              {entries.length > 0 
                ? `${Math.round((entries.filter(e => e.admissibilityStatus === 'ADMITTED').length / entries.length) * 100)}%`
                : '0%'}
            </span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <span>Refusal Rate</span>
            <span style={{
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              {entries.length > 0 
                ? `${Math.round((entries.filter(e => e.admissibilityStatus === 'REFUSED').length / entries.length) * 100)}%`
                : '0%'}
            </span>
          </div>
        </div>
      </div>

      {/* Proposal Lineage References */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          PROPOSAL LINEAGE REFERENCES
        </div>
        {entries.map(entry => (
          <div key={entry.proposalId} style={{
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
            marginBottom: SPACING.sp1,
            fontSize: TYPOGRAPHY.micro.size,
            fontFamily: TYPOGRAPHY.mono.family,
            color: NEUTRAL.text400,
          }}>
            {truncateHash(entry.lineageSource)}
          </div>
        ))}
      </div>

      {/* Individual Entries */}
      <div style={{ marginBottom: SPACING.sp3 }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          PROPOSAL ENTRIES
        </div>
        {entries.map(renderProposalEntry)}
      </div>

      {/* Read-only notice */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
      }}>
        <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>NOTE: </span>
        This proposal memory surface is read-only inspection only. It does not deploy, rollback, mutate state, grant execution authority, or perform autonomous learning. All entries require human review.
      </div>
    </div>
  );
};
